import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter, Inject,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  SimpleChanges,
  ViewChild,
  DOCUMENT
} from '@angular/core';
import { NgClass } from '@angular/common';
import { getNodeLabel } from '../../utils';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormInputComponent } from '../form-input/form-input.component';
import { AuthService } from '../../auth.service';
import {
  LoginFlow,
  RecoveryFlow,
  RegistrationFlow,
  SettingsFlow, UiNode, UiNodeInputAttributes, UiNodeScriptAttributes, UiText,
  UpdateLoginFlowBody,
  UpdateRecoveryFlowBody,
  UpdateRegistrationFlowBody,
  UpdateSettingsFlowBody,
  UpdateVerificationFlowBody,
  VerificationFlow
} from '../../../../sdk/kratos';
import NodeTypeEnum = UiNodeInputAttributes.NodeTypeEnum;
import { TurnstileComponent } from '../../../ui/turnstile/turnstile.component';

type Flow =
  | LoginFlow
  | RegistrationFlow
  | SettingsFlow
  | VerificationFlow
  | RecoveryFlow;

export type Values = Partial<
  | UpdateLoginFlowBody
  | UpdateRegistrationFlowBody
  | UpdateRecoveryFlowBody
  | UpdateSettingsFlowBody
  | UpdateVerificationFlowBody
>;

export type Methods =
  | "oidc"
  | "password"
  | "profile"
  | "totp"
  | "webauthn"
  | "passkey"
  | "link"
  | "lookup_secret";


@Component({
    selector: 'app-flow',
    host: {
      '[class.prominent-actions]': 'prominentActions'
    },
    imports: [
    FormInputComponent,
    NgClass,
    ReactiveFormsModule,
    TurnstileComponent
],
    templateUrl: './flow.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    styleUrl: './flow.component.less'
})
export class FlowComponent implements OnChanges, OnDestroy {
  @Input()
  flow!: Flow | undefined;

  @Input()
  hideGlobalMessages!: boolean;

  @Input()
  @Optional()
  only?: Methods;

  @Input()
  @Optional()
  enableCaptcha = false;

  @Input()
  @Optional()
  deferCaptcha = false;

  @Input()
  @Optional()
  prominentActions = false;

  @Output()
  submit: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild(TurnstileComponent)
  turnstile?: TurnstileComponent;

  uiNodes!: UiNode[]

  messages!: UiText[];

  nodeIds: string[] = [];

  hasNodeErrors = false;
  hasNodeSuccessMessages = false;

  protected readonly getNodeLabel = getNodeLabel;

  flowFormGroup!: FormGroup;

  isSubmitting = false;
  isCaptchaVisible = false;

  private scriptElementIdList: string[] = [];
  private submittedValues!: { [key: string]: any };

  constructor(private authService: AuthService,
              @Inject(DOCUMENT) private document: Document) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    const flowChange = changes['flow'];
    if (flowChange && flowChange.currentValue !== flowChange.previousValue) {
      if (!this.flow) {
        return;
      }

      this.isCaptchaVisible = this.enableCaptcha && !this.deferCaptcha;
      this.messages = this.flow.ui.messages || [];

      this.uiNodes = this.flow.ui.nodes.filter(({group}) => {
        if (!this.only) {
          return true;
        }
        return group === 'default' || group === this.only;
      });
      const nodeIdPrefix = `flow-${this.flow.id}-${this.only ?? 'all'}-node`;
      this.nodeIds = this.uiNodes.map((_, index) => `${nodeIdPrefix}-${index}`);
      this.hasNodeErrors = this.uiNodes.some(({messages}) =>
        messages.some(({type}) => type === 'error')
      );
      this.hasNodeSuccessMessages = this.uiNodes.some(({messages}) =>
        messages.some(({type}) => type === 'success')
      );
      const formControlsDict: { [name: string]: FormControl } = {};
      if (this.enableCaptcha) {
        formControlsDict['captchaToken'] = new FormControl(null, {validators: [Validators.required]});
      }
      for (const node of this.uiNodes) {
        if (node.attributes.node_type === NodeTypeEnum.Script) {
          this.runScripts(node.attributes as UiNodeScriptAttributes);
        } else if (node.attributes.node_type === NodeTypeEnum.Input) {
          const inputAttributes = node.attributes as UiNodeInputAttributes;
          if (inputAttributes.type !== 'submit' && inputAttributes.type !== 'button') {
            formControlsDict[inputAttributes.name] = new FormControl(inputAttributes.value);
            if (inputAttributes.required) {
              formControlsDict[inputAttributes.name].addValidators(Validators.required);
            }
          }
        }
      }
      this.flowFormGroup = new FormGroup<any>(formControlsDict);

      // When the flow re-renders after a submission (e.g. a validation error), the
      // previously issued captcha token has already been consumed. Reset the widget
      // so it emits a fresh token into the rebuilt form control, otherwise the
      // required captchaToken control stays null and submission is silently blocked.
      if (!flowChange.firstChange && this.enableCaptcha) {
        this.turnstile?.reset();
      }
    }
  }

  ngOnDestroy(): void {
    this.cleanUpScripts();
  }

  onSubmit(event: SubmitEvent): void {
    event.stopPropagation();
    event.preventDefault();
    // prevent double submission
    if (this.authService.isSubmitting) {
      return;
    }

    const submitter = event.submitter as HTMLInputElement;
    if (this.enableCaptcha && this.deferCaptcha && !this.isCaptchaVisible) {
      this.isCaptchaVisible = true;
      return;
    }

    const isResend = this.deferCaptcha && submitter.name === 'email';
    const invalidControls = Object.entries(this.flowFormGroup.controls)
      .filter(([name]) => !isResend || name !== 'code')
      .some(([, control]) => control.invalid);

    if (!invalidControls) {
      let values = this.flowFormGroup.value;
      this.submittedValues = Object.assign({}, values);
      // remove button and submit from values, there may be multiple buttons and submit in the form,
      // we should only have the clicked one.
      this.submittedValues[submitter.name] = submitter.value;
      this.uiNodes.filter(node => {
        const attributes = node.attributes as UiNodeInputAttributes;
        return attributes.node_type === NodeTypeEnum.Input
          && attributes.type !== 'hidden'
          && attributes.type !== 'checkbox'
          && attributes.type !== 'submit'
          && attributes.type !== 'button'
          && attributes.type !== 'number';
      })
        .forEach((node) => {
          const name = (node.attributes as UiNodeInputAttributes).name;
          if (typeof values[name] === 'string') {
            this.submittedValues[name] = values[name].trim();
          }
        });

      if (isResend) {
        delete this.submittedValues['code'];
      }

      this.flowFormGroup.markAsPristine();
      this.isSubmitting = true;

      if (!this.enableCaptcha) {
        this.doSubmit();
        return;
      }
      const captchaToken = this.submittedValues['captchaToken'];
      delete this.submittedValues['captchaToken'];
      this.submittedValues['transient_payload'] = {token: captchaToken};
      this.doSubmit();
    }
  }

  private doSubmit(): void {
    this.submit.emit(this.submittedValues);
    this.isSubmitting = false;
  }

  private runScripts(attributes: UiNodeScriptAttributes): void {
    const scriptElementId = `node/script/${attributes.id}`;
    this.scriptElementIdList.push(scriptElementId);
    const scriptElement = this.document.createElement('script');
    scriptElement.async = true;
    scriptElement.setAttribute("data-testid", scriptElementId);
    scriptElement.src = attributes.src;
    scriptElement.async = attributes.async;
    scriptElement.crossOrigin = attributes.crossorigin;
    scriptElement.integrity = attributes.integrity;
    scriptElement.referrerPolicy = attributes.referrerpolicy;
    scriptElement.type = attributes.type;

    this.document.body.appendChild(scriptElement);
  }

  private cleanUpScripts(): void {
    for (const scriptElementId of this.scriptElementIdList) {
      const scriptElement = this.document.body.querySelector(`[data-testId=${scriptElementId}]`);
      if (scriptElement) {
        this.document.body.removeChild(scriptElement);
      }
    }
  }
}
